/* eslint-disable max-lines */
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SupabaseClient } from '@supabase/supabase-js';

import type { Toast } from '@/hooks/use-toast';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import type { Database } from '@/integrations/supabase/types';
import { triggerDocumentProcessing } from '@/services/fileProcessingService';
import { getFileExtension } from '@/utils/fileUtils';

import {
  MAX_FILE_COUNT,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  SUPPORTED_FILE_EXTENSIONS,
} from '@/components/chat/fileAttachmentConstants';
import type {
  FileContext,
  ParsedFileResult,
} from '@/components/chat/parseAttachedFiles';
import { parseSingleFile } from '@/components/chat/parseAttachedFiles';

export type AttachedFileStatus =
  | 'parsing'
  | 'uploading'
  | 'parsed'
  | 'sent'
  | 'error';

const CONVERSATION_FILES_BUCKET = 'conversation-files';

export interface AttachedFile {
  id: string;
  file: File;
  status: AttachedFileStatus;
  parsedContent?: string;
  error?: string;
  storagePath?: string;
  storageBucket?: string;
  documentId?: string;
}

type ToastFn = (props: Toast) => void;
type SetAttachedFiles = Dispatch<SetStateAction<AttachedFile[]>>;

const validateFile = (file: File, toast: ToastFn): boolean => {
  const extension = getFileExtension(file.name);
  if (!SUPPORTED_FILE_EXTENSIONS.includes(extension)) {
    toast({
      title: 'Unsupported file type',
      description: `"${file.name}" is not a supported file type.`,
      variant: 'destructive',
    });
    return false;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    toast({
      title: 'File too large',
      description: `"${file.name}" exceeds the ${MAX_FILE_SIZE_LABEL} size limit.`,
      variant: 'destructive',
    });
    return false;
  }

  return true;
};

const updateFileStatus = (
  setAttachedFiles: SetAttachedFiles,
  fileId: string,
  update: Partial<AttachedFile>
) => {
  setAttachedFiles((previous) =>
    previous.map((attached) =>
      attached.id === fileId ? { ...attached, ...update } : attached
    )
  );
};

interface UploadFileParams {
  supabase: SupabaseClient<Database>;
  file: File;
  fileId: string;
  userId: string;
  conversationId: string;
}

const uploadFileToStorage = async ({
  supabase,
  file,
  fileId,
  userId,
  conversationId,
}: UploadFileParams): Promise<{
  storagePath: string;
  storageBucket: string;
} | null> => {
  const fileExt = getFileExtension(file.name) || 'bin';
  const storagePath = `${userId}/${conversationId}/${fileId}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from(CONVERSATION_FILES_BUCKET)
    .upload(storagePath, file, {
      upsert: true,
      metadata: { conversation_id: conversationId },
    });

  if (uploadError) {
    console.error(
      '[useFileAttachments] Storage upload failed:',
      uploadError.message
    );
    return null;
  }

  return { storagePath, storageBucket: CONVERSATION_FILES_BUCKET };
};

interface HandleUploadResultParams {
  setAttachedFiles: SetAttachedFiles;
  fileId: string;
  mountedRef: RefObject<boolean>;
  storageInfo: { storagePath: string; storageBucket: string } | null;
}

const handleUploadResult = ({
  setAttachedFiles,
  fileId,
  mountedRef,
  storageInfo,
}: HandleUploadResultParams) => {
  if (!mountedRef.current) {
    return;
  }
  if (!storageInfo) {
    updateFileStatus(setAttachedFiles, fileId, {
      status: 'error',
      error: 'Failed to upload file to storage',
    });
    return;
  }
  updateFileStatus(setAttachedFiles, fileId, {
    status: 'parsed',
    storagePath: storageInfo.storagePath,
    storageBucket: storageInfo.storageBucket,
  });
};

interface CreateDocumentRecordParams {
  supabase: SupabaseClient<Database>;
  conversationId: string;
  file: AttachedFile;
  userId: string;
}

const createDocumentRecord = async ({
  supabase,
  conversationId,
  file,
  userId,
}: CreateDocumentRecordParams): Promise<string | null> => {
  const fileType = getFileExtension(file.file.name) || null;

  const { data: docData, error: docError } = await supabase
    .from('document_files')
    .insert({
      conversation_id: conversationId,
      filename: file.file.name,
      file_type: fileType,
      file_size: file.file.size,
      storage_path: file.storagePath!,
      storage_bucket: file.storageBucket!,
      processing_status: 'pending' as const,
      created_by: userId,
      metadata: {
        original_filename: file.file.name,
        uploaded_at: new Date().toISOString(),
      },
    })
    .select('id')
    .single();

  if (docError) {
    console.error(
      '[useFileAttachments] Failed to create document record:',
      docError.message
    );
    return null;
  }

  return docData.id;
};

interface ProcessDocumentParams {
  file: AttachedFile;
  documentId: string;
  getToken: () => Promise<string | null>;
  setAttachedFiles: SetAttachedFiles;
  mountedRef: RefObject<boolean>;
}

const processDocument = async ({
  file,
  documentId,
  getToken,
  setAttachedFiles,
  mountedRef,
}: ProcessDocumentParams) => {
  if (!mountedRef.current) {
    return;
  }

  updateFileStatus(setAttachedFiles, file.id, { documentId });

  const clerkToken = await getToken();
  if (!clerkToken) {
    console.warn(
      '[useFileAttachments] No Clerk token available — skipping document processing'
    );
    updateFileStatus(setAttachedFiles, file.id, {
      status: 'error',
      error: 'Authentication unavailable — try removing and re-uploading',
    });
    return;
  }

  triggerDocumentProcessing(documentId, clerkToken).catch((processingError) => {
    console.error(
      `[useFileAttachments] Document processing failed for ${documentId}:`,
      processingError
    );
    updateFileStatus(setAttachedFiles, file.id, {
      status: 'error',
      error: 'File processing failed — try removing and re-uploading',
    });
  });
};

interface CreateAndProcessDocumentParams {
  supabase: SupabaseClient<Database>;
  conversationId: string;
  file: AttachedFile;
  userId: string;
  getToken: () => Promise<string | null>;
  setAttachedFiles: SetAttachedFiles;
  mountedRef: RefObject<boolean>;
}

const createAndProcessDocument = async ({
  supabase,
  conversationId,
  file,
  userId,
  getToken,
  setAttachedFiles,
  mountedRef,
}: CreateAndProcessDocumentParams) => {
  const documentId = await createDocumentRecord({
    supabase,
    conversationId,
    file,
    userId,
  });

  if (!documentId) {
    updateFileStatus(setAttachedFiles, file.id, {
      status: 'error',
      documentId: undefined,
      error: 'Failed to create document record',
    });
    return;
  }

  await processDocument({
    file,
    documentId,
    getToken,
    setAttachedFiles,
    mountedRef,
  });
};

interface HandleParseResultParams {
  setAttachedFiles: SetAttachedFiles;
  fileId: string;
  mountedRef: RefObject<boolean>;
  result: ParsedFileResult;
  supabase: SupabaseClient<Database>;
  file: File;
  userId?: string;
  conversationId?: string;
}

const handleParseResult = ({
  setAttachedFiles,
  fileId,
  mountedRef,
  result,
  supabase,
  file,
  userId,
  conversationId,
}: HandleParseResultParams) => {
  if (!mountedRef.current) {
    return;
  }

  const willUpload = !!(userId && conversationId);
  updateFileStatus(setAttachedFiles, fileId, {
    status: willUpload ? 'uploading' : 'parsed',
    parsedContent: result.content,
  });

  if (willUpload) {
    uploadFileToStorage({
      supabase,
      file,
      fileId,
      userId: userId!,
      conversationId: conversationId!,
    }).then((storageInfo) =>
      handleUploadResult({ setAttachedFiles, fileId, mountedRef, storageInfo })
    );
  }
};

interface TriggerParseParams {
  fileId: string;
  file: File;
  supabase: SupabaseClient<Database>;
  setAttachedFiles: SetAttachedFiles;
  mountedRef: RefObject<boolean>;
  userId?: string;
  conversationId?: string;
}

const triggerParse = ({
  fileId,
  file,
  supabase,
  setAttachedFiles,
  mountedRef,
  userId,
  conversationId,
}: TriggerParseParams) => {
  parseSingleFile(file, supabase).then(
    (result) =>
      handleParseResult({
        setAttachedFiles,
        fileId,
        mountedRef,
        result,
        supabase,
        file,
        userId,
        conversationId,
      }),
    (parseError) => {
      if (!mountedRef.current) {
        return;
      }
      updateFileStatus(setAttachedFiles, fileId, {
        status: 'error',
        error:
          parseError instanceof Error ? parseError.message : 'Parse failed',
      });
    }
  );
};

const DOCUMENT_ID_PLACEHOLDER = 'creating';

const buildParsedFileContexts = (
  attachedFiles: AttachedFile[]
): FileContext[] =>
  attachedFiles
    .filter(
      (attached) => attached.status === 'parsed' && !!attached.parsedContent
    )
    .map((attached) => ({
      id: attached.id,
      filename: attached.file.name,
      storagePath: attached.storagePath,
      storageBucket: attached.storageBucket,
      fileSize: attached.file.size,
      fileType: getFileExtension(attached.file.name) || undefined,
      documentId:
        attached.documentId === DOCUMENT_ID_PLACEHOLDER
          ? undefined
          : attached.documentId,
    }));

export interface UseFileAttachmentsReturn {
  attachedFiles: AttachedFile[];
  addFiles: (newFiles: FileList | File[]) => void;
  removeFile: (id: string) => void;
  retryFile: (id: string) => void;
  clearFiles: () => void;
  markFilesSent: () => string[];
  restoreFilesToParsed: (ids: string[]) => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
  openFilePicker: () => void;
  hasPendingFiles: boolean;
  hasErrorFiles: boolean;
  canSubmitFiles: boolean;
  getCanSubmitFiles: () => boolean;
  parsedFileContexts: FileContext[];
}

/* eslint-disable max-lines-per-function */
export const useFileAttachments = (
  supabase?: SupabaseClient<Database>,
  conversationId?: string | null
): UseFileAttachmentsReturn => {
  const { user, getToken } = useAuth();
  const userId = user?.id;
  const [attachedFiles, setAttachedFilesRaw] = useState<AttachedFile[]>([]);
  const filesRef = useRef<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mountedRef = useRef<boolean>(true);
  const { toast } = useToast();

  filesRef.current = attachedFiles;

  const setAttachedFiles = useCallback(
    (
      updater: AttachedFile[] | ((previous: AttachedFile[]) => AttachedFile[])
    ) => {
      const next =
        typeof updater === 'function' ? updater(filesRef.current) : updater;
      filesRef.current = next;
      setAttachedFilesRaw(next);
    },
    []
  );

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!conversationId || !userId || !supabase) {
      return;
    }

    const pendingUploads = filesRef.current.filter(
      (attached) =>
        (attached.status === 'parsed' || attached.status === 'sent') &&
        !attached.storagePath
    );

    if (pendingUploads.length === 0) {
      return;
    }

    for (const attached of pendingUploads) {
      updateFileStatus(setAttachedFiles, attached.id, { status: 'uploading' });
      uploadFileToStorage({
        supabase,
        file: attached.file,
        fileId: attached.id,
        userId,
        conversationId,
      }).then((storageInfo) =>
        handleUploadResult({
          setAttachedFiles,
          fileId: attached.id,
          mountedRef,
          storageInfo,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || !userId || !supabase) {
      return;
    }

    const uploadedWithoutDocument = filesRef.current.filter(
      (attached) =>
        (attached.status === 'parsed' || attached.status === 'sent') &&
        attached.storagePath &&
        !attached.documentId
    );

    if (uploadedWithoutDocument.length === 0) {
      return;
    }

    for (const attached of uploadedWithoutDocument) {
      updateFileStatus(setAttachedFiles, attached.id, {
        documentId: DOCUMENT_ID_PLACEHOLDER,
      });

      createAndProcessDocument({
        supabase,
        conversationId,
        file: attached,
        userId,
        getToken,
        setAttachedFiles,
        mountedRef,
      }).catch((error) => {
        console.error('[useFileAttachments] Document creation failed:', error);
      });
    }
  }, [
    attachedFiles,
    conversationId,
    userId,
    supabase,
    getToken,
    setAttachedFiles,
  ]);

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const incoming = Array.from(newFiles);

      const activeCount = filesRef.current.filter(
        (attached) => attached.status !== 'sent'
      ).length;

      if (activeCount + incoming.length > MAX_FILE_COUNT) {
        toast({
          title: 'Too many files',
          description: `You can attach up to ${MAX_FILE_COUNT} files.`,
          variant: 'destructive',
        });
        return;
      }

      const validFiles = incoming.filter((file) => validateFile(file, toast));
      if (validFiles.length === 0) {
        return;
      }

      const newAttached: AttachedFile[] = validFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: supabase ? 'parsing' : 'parsed',
      }));

      setAttachedFiles((previous) => [...previous, ...newAttached]);

      if (supabase) {
        newAttached.forEach((attached) => {
          triggerParse({
            fileId: attached.id,
            file: attached.file,
            supabase,
            setAttachedFiles,
            mountedRef,
            userId,
            conversationId: conversationId ?? undefined,
          });
        });
      }
    },
    [toast, supabase, setAttachedFiles, userId, conversationId]
  );

  const removeFile = useCallback(
    (id: string) => {
      setAttachedFiles((previous) =>
        previous.filter((attached) => attached.id !== id)
      );
    },
    [setAttachedFiles]
  );

  const retryFile = useCallback(
    (id: string) => {
      if (!supabase) {
        return;
      }

      const target = filesRef.current.find((attached) => attached.id === id);
      if (!target) {
        return;
      }

      const isRealDocumentId =
        target.documentId && target.documentId !== DOCUMENT_ID_PLACEHOLDER;

      if (isRealDocumentId) {
        const existingDocumentId = target.documentId as string;
        updateFileStatus(setAttachedFiles, id, {
          status: 'parsed',
          error: undefined,
        });
        getToken().then((clerkToken) => {
          if (!clerkToken) {
            updateFileStatus(setAttachedFiles, id, {
              status: 'error',
              error:
                'Authentication unavailable — try removing and re-uploading',
            });
            return;
          }
          triggerDocumentProcessing(existingDocumentId, clerkToken).catch(
            (processingError) => {
              console.error(
                `[useFileAttachments] Document processing failed for ${existingDocumentId}:`,
                processingError
              );
              updateFileStatus(setAttachedFiles, id, {
                status: 'error',
                error: 'File processing failed — try removing and re-uploading',
              });
            }
          );
        });
        return;
      }

      updateFileStatus(setAttachedFiles, id, {
        status: 'parsing',
        error: undefined,
      });

      triggerParse({
        fileId: id,
        file: target.file,
        supabase,
        setAttachedFiles,
        mountedRef,
        userId,
        conversationId: conversationId ?? undefined,
      });
    },
    [supabase, setAttachedFiles, userId, conversationId, getToken]
  );

  const clearFiles = useCallback(() => {
    setAttachedFiles([]);
  }, [setAttachedFiles]);

  const markFilesSent = useCallback((): string[] => {
    const transitionedIds = filesRef.current
      .filter((attached) => attached.status === 'parsed')
      .map((attached) => attached.id);

    if (transitionedIds.length === 0) {
      return [];
    }

    const transitionedIdSet = new Set(transitionedIds);
    setAttachedFiles((previous) =>
      previous.map((attached) =>
        transitionedIdSet.has(attached.id)
          ? { ...attached, status: 'sent' as const }
          : attached
      )
    );
    return transitionedIds;
  }, [setAttachedFiles]);

  const restoreFilesToParsed = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }
      const idSet = new Set(ids);
      setAttachedFiles((previous) =>
        previous.map((attached) =>
          idSet.has(attached.id) && attached.status === 'sent'
            ? { ...attached, status: 'parsed' as const }
            : attached
        )
      );
    },
    [setAttachedFiles]
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const hasPendingFiles = useMemo(
    () =>
      attachedFiles.some(
        (attached) =>
          attached.status === 'parsing' || attached.status === 'uploading'
      ),
    [attachedFiles]
  );

  const hasErrorFiles = useMemo(
    () => attachedFiles.some((attached) => attached.status === 'error'),
    [attachedFiles]
  );

  const canSubmitFiles = !hasPendingFiles && !hasErrorFiles;

  const getCanSubmitFiles = useCallback(() => {
    return filesRef.current.every(
      (attached) =>
        attached.status !== 'parsing' &&
        attached.status !== 'uploading' &&
        attached.status !== 'error'
    );
  }, []);

  const parsedFileContexts: FileContext[] = useMemo(
    () => buildParsedFileContexts(attachedFiles),
    [attachedFiles]
  );

  return {
    attachedFiles,
    addFiles,
    removeFile,
    retryFile,
    clearFiles,
    markFilesSent,
    restoreFilesToParsed,
    fileInputRef,
    openFilePicker,
    hasPendingFiles,
    hasErrorFiles,
    canSubmitFiles,
    getCanSubmitFiles,
    parsedFileContexts,
  };
};
